import { ClipboardEvent, KeyboardEvent } from 'react';
import { Button, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material';

import { REVO_GRID_FILTER_OPERATOR_LABELS, operatorNeedsValue } from './filtering';
import { RevoGridColumnFilter, RevoGridFilterOperator } from './types';

type RevoGridFilterPanelProps = {
  open: boolean;
  columnName: string;
  anchorPosition: { top: number; left: number };
  operators: readonly RevoGridFilterOperator[];
  filter: RevoGridColumnFilter;
  onChange: (filter: RevoGridColumnFilter) => void;
  onReset: () => void;
  onClose: () => void;
};

const PANEL_WIDTH = 260;

export const RevoGridFilterPanel = ({
  open,
  columnName,
  anchorPosition,
  operators,
  filter,
  onChange,
  onReset,
  onClose,
}: RevoGridFilterPanelProps) => {
  const onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const onClipboard = (event: ClipboardEvent) => event.stopPropagation();

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      onClose={onClose}
      PaperProps={{
        onKeyDown,
        onPaste: onClipboard,
        onCopy: onClipboard,
        onCut: onClipboard,
        sx: { width: PANEL_WIDTH },
      }}
    >
      <Stack spacing={2} sx={{ p: 2 }} data-sm="revo-grid-filter-panel">
        <Typography variant="subtitle2" noWrap title={columnName}>
          Filter by {columnName}
        </Typography>

        <TextField
          select
          size="small"
          label="Condition"
          value={filter.operator}
          onChange={(event) =>
            onChange({ ...filter, operator: event.target.value as RevoGridFilterOperator })
          }
          inputProps={{ 'data-sm': 'revo-grid-filter-operator' }}
        >
          {operators.map((operator) => (
            <MenuItem key={operator} value={operator}>
              {REVO_GRID_FILTER_OPERATOR_LABELS[operator]}
            </MenuItem>
          ))}
        </TextField>

        {operatorNeedsValue(filter.operator) && (
          <TextField
            size="small"
            autoFocus
            autoComplete="off"
            placeholder="Enter value..."
            value={filter.value}
            onChange={(event) => onChange({ ...filter, value: event.target.value })}
            inputProps={{ 'data-sm': 'revo-grid-filter-value' }}
          />
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={onReset}
            data-sm="revo-grid-filter-reset"
          >
            Reset
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onClose}
            data-sm="revo-grid-filter-close"
          >
            Close
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
